/**
 * Admin Product Catalog API service (PA-2)
 *
 * Covers AdminProductController, AdminCatalogController,
 * AdminSubscriptionController, AdminOrderController, AdminPricingController.
 *
 * @see docs/specifications/2026-03-08-billing-admin-ui.md
 */
import apiClient from "./apiClient";
import type {
  BillingProduct,
  CreateProductDto,
  UpdateProductDto,
  TransitionProductStatusDto,
  ProductFeature,
  AddFeatureDto,
  UpdateFeatureDto,
  LeafProductPickerItem,
  CatalogEntry,
  CreateCatalogEntryDto,
  UpdateCatalogEntryDto,
  BillingSubscription,
  BillingSubscriptionDetail,
  AdminRenewalPreferenceTarget,
  AdminRenewalPreferenceResult,
  Entitlement,
  BillingOrder,
  BillingOrderDetail,
  PricingCalculator,
  PricingComponent,
  PricingComponentVersion,
  CreateComponentVersionDto,
  CloseVersionDto,
  PricingPreviewResponse,
  PackagePricingRequest,
  PackagePricingResponse,
  PaginatedResponse,
  CalculatorType,
  StripeLinkResponse,
  UnmatchedPaymentDto,
  OrderSearchResult,
  AttachPaymentDto,
  LineDescriptionPreviewResponse,
} from "../types/adminBilling";
import type { OrderStatus } from "../components/billing/OrderStatusBadge";

// =============================================================================
// PRODUCTS
// =============================================================================

export const listProducts = async (
  brandId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<BillingProduct>> => {
  const { data } = await apiClient.get("/admin/billing/products", {
    params: { brandId, limit: params.limit ?? 20, offset: params.offset ?? 0 },
  });
  return data;
};

export const getProduct = async (
  id: string,
  brandId: string
): Promise<BillingProduct> => {
  const { data } = await apiClient.get(`/admin/billing/products/${id}`, {
    params: { brandId },
  });
  return data;
};

export const createProduct = async (
  dto: CreateProductDto
): Promise<BillingProduct> => {
  const { data } = await apiClient.post("/admin/billing/products", dto);
  return data;
};

export const updateProduct = async (
  id: string,
  brandId: string,
  dto: UpdateProductDto
): Promise<BillingProduct> => {
  const { data } = await apiClient.patch(`/admin/billing/products/${id}`, dto, {
    params: { brandId },
  });
  return data;
};

export const transitionProductStatus = async (
  id: string,
  brandId: string,
  dto: TransitionProductStatusDto
): Promise<{ id: string; status: string; updatedAt: string }> => {
  const { data } = await apiClient.post(
    `/admin/billing/products/${id}/status`,
    dto,
    { params: { brandId } }
  );
  return data;
};

export const addProductFeature = async (
  productId: string,
  brandId: string,
  dto: AddFeatureDto
): Promise<ProductFeature> => {
  const { data } = await apiClient.post(
    `/admin/billing/products/${productId}/features`,
    dto,
    { params: { brandId } }
  );
  return data;
};

export const updateProductFeature = async (
  productId: string,
  featureId: string,
  brandId: string,
  dto: UpdateFeatureDto
): Promise<ProductFeature> => {
  const { data } = await apiClient.patch(
    `/admin/billing/products/${productId}/features/${featureId}`,
    dto,
    { params: { brandId } }
  );
  return data;
};

export const removeProductFeature = async (
  productId: string,
  featureId: string,
  brandId: string
): Promise<void> => {
  await apiClient.delete(
    `/admin/billing/products/${productId}/features/${featureId}`,
    { params: { brandId } }
  );
};

// =============================================================================
// LEAF PRODUCT PICKER (C10)
// =============================================================================

export const listLeafProducts = async (
  brandId: string
): Promise<LeafProductPickerItem[]> => {
  const { data } = await apiClient.get("/admin/billing/products/leaves", {
    params: { brandId },
  });
  return data;
};

// =============================================================================
// CATALOG ENTRIES
// =============================================================================

export const listCatalogEntries = async (
  brandId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<CatalogEntry>> => {
  const { data } = await apiClient.get("/admin/billing/catalog", {
    params: { brandId, limit: params.limit ?? 20, offset: params.offset ?? 0 },
  });
  return data;
};

export const getCatalogEntry = async (
  id: string,
  brandId: string
): Promise<CatalogEntry> => {
  const { data } = await apiClient.get(`/admin/billing/catalog/${id}`, {
    params: { brandId },
  });
  return data;
};

export const createCatalogEntry = async (
  dto: CreateCatalogEntryDto
): Promise<CatalogEntry> => {
  const { data } = await apiClient.post("/admin/billing/catalog", dto);
  return data;
};

export const updateCatalogEntry = async (
  id: string,
  brandId: string,
  dto: UpdateCatalogEntryDto
): Promise<CatalogEntry> => {
  const { data } = await apiClient.patch(`/admin/billing/catalog/${id}`, dto, {
    params: { brandId },
  });
  return data;
};

export const deleteCatalogEntry = async (
  id: string,
  brandId: string
): Promise<void> => {
  await apiClient.delete(`/admin/billing/catalog/${id}`, {
    params: { brandId },
  });
};

// =============================================================================
// SUBSCRIPTIONS
// =============================================================================

export const listBillingSubscriptions = async (
  brandId: string,
  params: { limit?: number; offset?: number; tenantId?: string } = {}
): Promise<PaginatedResponse<BillingSubscription>> => {
  const { data } = await apiClient.get("/admin/billing/subscriptions", {
    params: {
      brandId,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
      // Only include tenantId when provided so the legacy "all tenants" call
      // is unchanged (backend treats undefined as no AND filter).
      ...(params.tenantId ? { tenantId: params.tenantId } : {}),
    },
  });
  return data;
};

export const getBillingSubscription = async (
  id: string,
  brandId: string
): Promise<BillingSubscriptionDetail> => {
  const { data } = await apiClient.get(`/admin/billing/subscriptions/${id}`, {
    params: { brandId },
  });
  return data;
};

export const reDeriveBillingSubscription = async (
  id: string,
  brandId: string
): Promise<{ subscriptionId: string; entitlements: Entitlement[] }> => {
  const { data } = await apiClient.post(
    `/admin/billing/subscriptions/${id}/sync-capabilities`,
    undefined,
    { params: { brandId } }
  );
  return data;
};

export const migrateBillingSubscription = async (
  id: string,
  productId: string,
  brandId: string
): Promise<{
  subscriptionId: string;
  productInstance: {
    id: string;
    snapshotName: string;
    snapshotProductType: string;
    createdAt: string;
  };
  entitlementsCount: number;
}> => {
  const { data } = await apiClient.post(
    `/admin/billing/subscriptions/${id}/migrate`,
    { productId },
    { params: { brandId } }
  );
  return data;
};

/**
 * Toggle auto-renewal for a billing subscription (AC-6, AC-19)
 * PATCH /admin/billing/subscriptions/:id/auto-renewal?brandId=...
 * brandId query param is required for IDOR prevention (JOIN verifies product.brand_id = brandId).
 *
 * @param id - Subscription UUID
 * @param autoRenewal - New auto-renewal value
 * @param brandId - Brand UUID (required query param for scope verification)
 * @returns Object with updated autoRenewal value
 */
export const updateAdminAutoRenewal = async (
  id: string,
  autoRenewal: boolean,
  brandId: string
): Promise<{ autoRenewal: boolean }> => {
  const { data } = await apiClient.patch(
    `/admin/billing/subscriptions/${id}/auto-renewal`,
    { autoRenewal },
    { params: { brandId } }
  );
  return data;
};

/**
 * Cancel a billing subscription (AC-14c).
 * POST /admin/billing/subscriptions/:id/cancel?brandId=...
 * Today used from the admin UI only for SCHEDULED subscriptions; the
 * endpoint itself also serves ACTIVE/TRIALING cancellation (AC-14b guard).
 */
export const cancelBillingSubscription = async (
  id: string,
  brandId: string,
  reason: string
): Promise<{ id: string; tenantId: string; status: string; canceledAt: string }> => {
  const { data } = await apiClient.post(
    `/admin/billing/subscriptions/${id}/cancel`,
    { reason },
    { params: { brandId } }
  );
  return data;
};

const toRenewalPreferenceResult = (
  status: number,
  data: AdminRenewalPreferenceResult
): AdminRenewalPreferenceResult => ({
  productId: data.productId,
  billingInterval: data.billingInterval,
  proformaPending: status === 202 || data.proformaPending === true,
});

/**
 * Set the package + interval for a subscription's next billing period
 * (wybor-pakietu-na-kolejny-okres AC-12).
 * PUT /admin/billing/subscriptions/:id/renewal-preference?brandId=...
 * brandId query param is required for IDOR prevention (product.brand_id = brandId).
 *
 * HTTP errors propagate as axios errors: 400 (target not selectable), 404 (feature
 * disabled or subscription not found), 409 (AC-5 block, `errorCode` in body).
 *
 * @returns Effective renewal target; `proformaPending` on HTTP 202 (AC-7)
 */
export const setAdminRenewalPreference = async (
  id: string,
  target: AdminRenewalPreferenceTarget,
  brandId: string
): Promise<AdminRenewalPreferenceResult> => {
  const response = await apiClient.put<AdminRenewalPreferenceResult>(
    `/admin/billing/subscriptions/${id}/renewal-preference`,
    { productId: target.productId, billingInterval: target.billingInterval },
    { params: { brandId } }
  );
  return toRenewalPreferenceResult(response.status, response.data);
};

/**
 * Clear the renewal preference — the next period renews on the default target
 * (wybor-pakietu-na-kolejny-okres AC-12). Same blocks/errors as {@link setAdminRenewalPreference}.
 * DELETE /admin/billing/subscriptions/:id/renewal-preference?brandId=...
 */
export const clearAdminRenewalPreference = async (
  id: string,
  brandId: string
): Promise<AdminRenewalPreferenceResult> => {
  const response = await apiClient.delete<AdminRenewalPreferenceResult>(
    `/admin/billing/subscriptions/${id}/renewal-preference`,
    { params: { brandId } }
  );
  return toRenewalPreferenceResult(response.status, response.data);
};

// =============================================================================
// ORDERS
// =============================================================================

export const listBillingOrders = async (
  brandId: string,
  params: {
    limit?: number;
    offset?: number;
    tenantId?: string;
    /** Multi-select status filter (AC-6) — sent as a real array, not CSV. */
    status?: OrderStatus[];
  } = {}
): Promise<PaginatedResponse<BillingOrder>> => {
  const { data } = await apiClient.get("/admin/billing/orders", {
    params: {
      brandId,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
      ...(params.tenantId ? { tenantId: params.tenantId } : {}),
      ...(params.status ? { status: params.status } : {}),
    },
  });
  return data;
};

export const getBillingOrder = async (
  id: string,
  brandId: string
): Promise<BillingOrderDetail> => {
  const { data } = await apiClient.get(`/admin/billing/orders/${id}`, {
    params: { brandId },
  });
  return data;
};

// =============================================================================
// ORDER ACTIONS
// =============================================================================

/** Cancel an order (POST /admin/billing/orders/:id/cancel) */
export const cancelBillingOrder = async (
  id: string,
  brandId: string,
  cancelReason: string
): Promise<{ orderId: string; canceled: boolean }> => {
  const { data } = await apiClient.post(`/admin/billing/orders/${id}/cancel`, {
    brandId,
    cancelReason,
  });
  return data;
};

/** Register payment for an order (POST /admin/billing/orders/:id/register-payment) */
export const registerBillingPayment = async (
  id: string,
  params: {
    brandId: string;
    amountCents: number;
    source: string;
    receivedAt: string;
    documentId: string;
    externalReference?: string;
  }
): Promise<{ paymentId: string; allocationId: string; isFullyPaid: boolean }> => {
  const { data } = await apiClient.post(
    `/admin/billing/orders/${id}/register-payment`,
    params
  );
  return data;
};

/** Confirm an order and trigger fulfillment (POST /admin/billing/orders/:id/confirm) */
export const confirmBillingOrder = async (
  id: string,
  brandId: string
): Promise<{ orderId: string; subscriptionId: string }> => {
  const { data } = await apiClient.post(`/admin/billing/orders/${id}/confirm`, {
    brandId,
  });
  return data;
};

/** DTO for creating a new MANUAL billing order */
export interface CreateBillingOrderDto {
  tenantId: string;
  brandId: string;
  paymentMethod: "BANK_TRANSFER" | "STRIPE" | null;
  line: {
    productId: string;
    /** Billing interval: monthly | quarterly | yearly */
    billingInterval: "monthly" | "quarterly" | "yearly";
    periodStart: string;
    /** Omit when customDurationDays is set — backend derives periodEnd from it (AC-3). */
    periodEnd?: string;
    /** Omitted unless manually edited — the server composes it otherwise (AC-5, AC-8). */
    description?: string;
    sortOrder?: number;
    /** Custom first-cycle length in days (AC-6). When set, periodEnd is omitted. */
    customDurationDays?: number;
    /**
     * Initial auto-renewal of the NEW subscription (omit = default true).
     * Backend returns 409 when the tenant already has an ACTIVE/TRIALING one.
     */
    autoRenewal?: boolean;
  };
}

/** Create a new MANUAL billing order (POST /admin/billing/orders) */
export const createBillingOrder = async (
  dto: CreateBillingOrderDto
): Promise<{ id: string; orderNumber: string; status: string }> => {
  const { data } = await apiClient.post("/admin/billing/orders", dto);
  return data;
};

/** Request params for the order-line description preview (AC-6). */
export interface LineDescriptionPreviewRequestParams {
  brandId: string;
  productId: string;
  billingInterval: "monthly" | "quarterly" | "yearly";
  periodStart: string;
  customDurationDays?: number;
}

/** AC-7 Technical Constraints: 5s timeout for the preview request. */
export const LINE_DESCRIPTION_PREVIEW_TIMEOUT_MS = 5000;

/**
 * Live preview of the order-line description + derived period end (AC-6, AC-7).
 * Read-only: no side effects server-side.
 */
export const getLineDescriptionPreview = async (
  params: LineDescriptionPreviewRequestParams,
  signal?: AbortSignal
): Promise<LineDescriptionPreviewResponse> => {
  const { data } = await apiClient.get(
    "/admin/billing/orders/line-description-preview",
    { params, signal, timeout: LINE_DESCRIPTION_PREVIEW_TIMEOUT_MS }
  );
  return data;
};

/** Price an order automatically (POST /admin/billing/orders/:id/price) */
export const priceBillingOrder = async (
  orderId: string,
  brandId: string
): Promise<{ orderId: string; totalNetCents: number; totalGrossCents: number }> => {
  const { data } = await apiClient.post(
    `/admin/billing/orders/${orderId}/price`,
    { brandId }
  );
  return data;
};

/** Generate a proforma document for an order (POST /admin/billing/orders/:id/generate-proforma) */
export const generateBillingProforma = async (
  orderId: string,
  brandId: string,
  /** Manual due date override (AC-4/AC-2b), YYYY-MM-DD. Omit for the server default. */
  dueDate?: string
): Promise<{ documentId: string; documentNumber: string }> => {
  const { data } = await apiClient.post(
    `/admin/billing/orders/${orderId}/generate-proforma`,
    { brandId, ...(dueDate ? { dueDate } : {}) }
  );
  return data;
};

/** Set arbitrary (manual) price on a specific order line (POST /admin/billing/orders/:id/lines/:lineId/arbitrary-price) */
export const setArbitraryLinePrice = async (params: {
  orderId: string;
  lineId: string;
  brandId: string;
  netCents: number;
  arbitraryReason: string;
}): Promise<{ orderId: string; lineId: string; netCents: number }> => {
  const { orderId, lineId, brandId, netCents, arbitraryReason } = params;
  const { data } = await apiClient.post(
    `/admin/billing/orders/${orderId}/lines/${lineId}/arbitrary-price`,
    { brandId, netCents, arbitraryReason }
  );
  return data;
};

/**
 * Reverse a recorded payment (POST /admin/billing/payments/:id/reverse).
 *
 * AC-A4: body { reason: string }, optional ?force=true for REVERSAL_TOO_LATE override.
 * Returns 200 on success; 409 if status != RECORDED; 409 REVERSAL_TOO_LATE if > 180 days.
 *
 * Backend returns `{ paymentId; status: 'REVERSED' }` (see PaymentReversalService
 * `ReversalResult`). The previous `{ paymentId; reversed: boolean }` shape never
 * matched and was caught by the consolidated review (Code Quality MEDIUM).
 */
export const reversePayment = async (
  paymentId: string,
  params: { reason: string; force?: boolean }
): Promise<{ paymentId: string; status: "REVERSED" }> => {
  const { data } = await apiClient.post(
    `/admin/billing/payments/${paymentId}/reverse`,
    { reason: params.reason },
    params.force ? { params: { force: "true" } } : undefined
  );
  return data;
};

// =============================================================================
// PHASE B — STRIPE PAYMENT LINK + UNMATCHED PAYMENTS (AC-B1, AC-B4)
// =============================================================================

/**
 * Issue a Stripe Checkout Session for an order (AC-B1).
 * POST /admin/billing/orders/:id/stripe-payment-link
 * Returns { url, sessionId, expiresAt }.
 */
export const issueStripePaymentLink = async (
  orderId: string,
  brandId: string
): Promise<StripeLinkResponse> => {
  const { data } = await apiClient.post(
    `/admin/billing/orders/${orderId}/stripe-payment-link`,
    { brandId }
  );
  return data;
};

/**
 * List unmatched payments (AC-B4).
 * GET /admin/billing/payments?matchStatus=UNMATCHED&receivedAfter=...
 */
export const listUnmatchedPayments = async (params: {
  matchStatus?: string;
  receivedAfter?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<UnmatchedPaymentDto>> => {
  const { data } = await apiClient.get("/admin/billing/payments", {
    params: {
      matchStatus: params.matchStatus ?? "UNMATCHED",
      receivedAfter: params.receivedAfter,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
    },
  });
  return data;
};

/**
 * Search orders for the AttachPaymentModal search input (C-10).
 * Reuses existing GET /admin/billing/orders?search=&limit=10
 */
export const searchOrdersForAttach = async (params: {
  brandId?: string;
  tenantId?: string;
  search?: string;
  limit?: number;
}): Promise<OrderSearchResult[]> => {
  const { data } = await apiClient.get("/admin/billing/orders", {
    params: {
      ...(params.brandId ? { brandId: params.brandId } : {}),
      ...(params.tenantId ? { tenantId: params.tenantId } : {}),
      ...(params.search ? { search: params.search } : {}),
      limit: params.limit ?? 10,
      offset: 0,
    },
  });
  // Backend returns PaginatedResponse<BillingOrder>; map to OrderSearchResult
  // (backend may enrich with proformaDocumentId — we pass through whatever comes back)
  return (data?.data ?? data) as OrderSearchResult[];
};

/**
 * Attach an unmatched payment to an order (AC-B4).
 * POST /admin/billing/payments/:paymentId/attach
 * Body: { orderId, documentId? }
 * 200 idempotent → close modal + toast.
 * 409 → friendly error (error.code in response body).
 */
export const attachPayment = async (
  paymentId: string,
  dto: AttachPaymentDto
): Promise<{ paymentId: string; orderId: string; allocationId?: string }> => {
  const { data } = await apiClient.post(
    `/admin/billing/payments/${paymentId}/attach`,
    dto
  );
  return data;
};

// =============================================================================
// PRICING
// =============================================================================

export const listPricingComponents = async (
  brandId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<PricingComponent>> => {
  const { data } = await apiClient.get("/admin/billing/pricing", {
    params: { brandId, limit: params.limit ?? 20, offset: params.offset ?? 0 },
  });
  return data;
};

export const getPricingComponent = async (
  id: string,
  brandId: string
): Promise<PricingComponent> => {
  const { data } = await apiClient.get(`/admin/billing/pricing/${id}`, {
    params: { brandId },
  });
  return data;
};

export const listCalculators = async (
  brandId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<PricingCalculator>> => {
  const { data } = await apiClient.get("/admin/billing/pricing/calculators", {
    params: { brandId, limit: params.limit ?? 20, offset: params.offset ?? 0 },
  });
  return data;
};

// =============================================================================
// PRICING — VERSION MUTATIONS (2026-05-05 admin pricing UI)
// =============================================================================

/**
 * AC-1: POST /admin/billing/pricing/components/:id/versions
 * When `closeExistingVersionId` is provided, the backend runs an atomic
 * replacement transaction (close existing + insert new in one transaction).
 */
export const addPricingComponentVersion = async (
  componentId: string,
  brandId: string,
  dto: CreateComponentVersionDto,
  options?: {
    closeExistingVersionId?: string;
    closeExistingVersion?: number;
  }
): Promise<PricingComponentVersion> => {
  const { data } = await apiClient.post(
    `/admin/billing/pricing/components/${componentId}/versions`,
    dto,
    {
      params: {
        brandId,
        ...(options?.closeExistingVersionId && {
          closeExistingVersionId: options.closeExistingVersionId,
        }),
        ...(options?.closeExistingVersion !== undefined && {
          closeExistingVersion: options.closeExistingVersion,
        }),
      },
    }
  );
  return data;
};

/** AC-3: PATCH /admin/billing/pricing/versions/:id (close-version, validTo only). */
export const closePricingComponentVersion = async (
  versionId: string,
  brandId: string,
  dto: CloseVersionDto
): Promise<PricingComponentVersion> => {
  const { data } = await apiClient.patch(
    `/admin/billing/pricing/versions/${versionId}`,
    dto,
    { params: { brandId } }
  );
  return data;
};

/** AC-5: DELETE /admin/billing/pricing/versions/:id */
export const deletePricingComponentVersion = async (
  versionId: string,
  brandId: string,
  version: number
): Promise<void> => {
  await apiClient.delete(`/admin/billing/pricing/versions/${versionId}`, {
    params: { brandId, version },
  });
};

/** AC-7: POST /admin/billing/pricing/calculators */
export interface CreateCalculatorDto {
  name: string;
  type: CalculatorType;
  config: Record<string, unknown>;
  description?: string;
}

export const createPricingCalculator = async (
  brandId: string,
  dto: CreateCalculatorDto
): Promise<PricingCalculator> => {
  const { data } = await apiClient.post(
    "/admin/billing/pricing/calculators",
    dto,
    { params: { brandId } }
  );
  return data;
};

// =============================================================================
// PRICING PREVIEW
// =============================================================================

export const getProductPricingPreview = async (
  productId: string,
  brandId: string
): Promise<PricingPreviewResponse> => {
  const { data } = await apiClient.get(
    `/admin/billing/products/${productId}/pricing-preview`,
    { params: { brandId } }
  );
  return data;
};

// =============================================================================
// PACKAGE PRICING (Configurator)
// =============================================================================

export const getPackagePricing = async (
  productId: string,
  request: PackagePricingRequest
): Promise<PackagePricingResponse> => {
  const { data } = await apiClient.post(
    `/admin/billing/products/${productId}/package-pricing`,
    request
  );
  return data;
};

// =============================================================================
// TENANT BILLING MIGRATION (AC-A5, AC-A6, AC-A7)
// =============================================================================

/**
 * Company data from GUS registry.
 * Null when GUS is unavailable or tenant has no NIP.
 */
export interface MigrationPreviewCompanyData {
  name: string;
  street: string;
  city: string;
  postalCode: string;
}

/** Subscription entry in migration preview */
export interface MigrationPreviewSubscription {
  id: string;
  oldStatus: string;
  newStatus: string;
  billingPeriod: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  stripeSubscriptionId: string | null;
}

/** Document entry in migration preview */
export interface MigrationPreviewDocument {
  type: 'VAT_INVOICE' | 'PROFORMA';
  number: string;
  amountGross: number;
  issueDate: string;
}

/** Response for GET /admin/billing/migrate-tenant/:tenantId/preview */
export interface MigrationPreviewDto {
  tenantId: string;
  tenantName: string;
  nip: string;
  companyData: MigrationPreviewCompanyData | null;
  subscriptions: MigrationPreviewSubscription[];
  documents: MigrationPreviewDocument[];
  warnings: string[];
  canMigrate: boolean;
  blockingReasons: string[];
}

/** Single subscription mapping in migration result */
export interface MigrationResultSubscription {
  oldSubscriptionId: string;
  newSubscriptionId: string;
  orderId: string;
  orderNumber: string;
  status: string;
}

/** Response for POST /admin/billing/migrate-tenant/:tenantId */
export interface MigrationResultDto {
  tenantId: string;
  partyId: string;
  subscriptionsMigrated: MigrationResultSubscription[];
  documentsImported: number;
  warnings: string[];
  migratedAt: string;
  alreadyMigrated: boolean;
}

/**
 * Get migration preview for a tenant (AC-A2).
 * May take up to 35s (GUS + Fakturownia calls).
 */
export const getMigrationPreview = async (
  tenantId: string
): Promise<MigrationPreviewDto> => {
  const { data } = await apiClient.get(
    `/admin/billing/migrate-tenant/${tenantId}/preview`
  );
  return data;
};

/**
 * Execute tenant billing migration (AC-A1).
 * May take up to 120s. Creates Party, Orders, Subscriptions, imports documents.
 */
export const executeTenantMigration = async (
  tenantId: string
): Promise<MigrationResultDto> => {
  const { data } = await apiClient.post(
    `/admin/billing/migrate-tenant/${tenantId}`
  );
  return data;
};
